import {
  useGetEscalationContactsQuery,
  useSetEscalationContactsMutation,
} from 'api/escalationContacts'
import type { EscalationContacts } from 'api/escalationContacts.types'
import type { MerchantUser } from 'api/merchantUsers'
import { merchantUsersApi } from 'api/merchantUsers'
import {
  useGetRecoveryConfigQuery,
  useUpdateRecoveryConfigMutation,
} from 'api/recoveryAi'
import useAdminPredicate from 'hooks/useAdminPredicate'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAbility } from 'utilities/ability'
import type { ConfigRowSaveAdapter } from './configRowSaveAdapter'
import { useHydratedContactSelection } from './useHydratedContactSelection'

// A 404/403 on the contacts GET is the DEMOTED defensive edge case (recovery-ai
// flag off / no RBAC view / pre-deploy env) — the resource is live, so this is
// an availability edge, not the default. Hosts surface a notice + disable Save
// rather than letting a Save silently no-op. A 500 is deliberately NOT demoted.
const isContactsUnavailable = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return false
  }
  const { status } = error as { status: unknown }
  return status === 403 || status === 404
}

// Order-INSENSITIVE set equality — deliberately not useHydratedContactSelection's
// order-sensitive `sameIds`, which backs a hydration bailout where reference
// stability is all that matters. Reordering the same recipients is not an edit,
// so it must not arm Save.
const sameIdSet = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false
  const known = new Set(a)
  return b.every(id => known.has(id))
}

/** 'staged' folds the toggle into commit(); 'eager' persists it on click. */
export type EscalationContactsNotifyMode = 'staged' | 'eager'
/** 'staged' persists recipients on commit(); 'eager' writes per change. */
export type EscalationContactsRecipientMode = 'staged' | 'eager'

export type EscalationContactsControllerOptions = {
  notifyMode: EscalationContactsNotifyMode
  /** Defaults to 'staged'. Only the wizard passes 'eager' (it has no Save button). */
  recipientMode?: EscalationContactsRecipientMode
  /**
   * Shared RTK Query mutation cache keys, so an observer outside this hook's
   * tree (the wizard shell's completeness gate) can watch a write the step
   * issued. Import the constants; never inline a literal at a call site.
   *
   * ⚠ BOTH keys are required together, and that is a money-safety constraint, not
   * tidiness: the gate has one leg per key, and an unwatched leg fails OPEN. With
   * `config` missing, `isSavingNotify` is false forever, so a notify-OFF write in
   * flight leaves Finalize enabled over a cache that still reads `true` — a
   * merchant activates with no reachable human.
   *
   * Two guards, not one. The type makes passing a single key impossible; passing two
   * that DISAGREE with the observer's is caught by
   * useActivationCompleteness.mutationCable.test.ts (severing either key fails it).
   */
  saveKeys?: { contacts: string; config: string }
}

export type EscalationContactsController = ConfigRowSaveAdapter & {
  // Inputs for EscalationContactsBody.
  users: MerchantUser[]
  primaryIds: number[]
  additionalIds: number[]
  notifyLocationManagers: boolean
  canManage: boolean
  /**
   * Which of `users` are admins, for the pickers' ADMINS / ALL OTHER USERS split
   * (#3883). RBAC-aware, so it cannot be derived from the row alone.
   *
   *   context: resolved HERE rather than in each host. #3883 put it in the pane
   *   because the pane was the picker's only consumer; there are now three, all
   *   reached through one shared body, so resolving it per host would be the exact
   *   drift this controller exists to remove. It stays out of the picker and the
   *   body, both of which are contractually fetch-free.
   */
  isUserAdmin: (user: MerchantUser) => boolean
  /**
   * The roles lookup behind isUserAdmin could not resolve, so EVERY RBAC admin
   * demotes into ALL OTHER USERS with no badge. Hosts surface a non-blocking
   * notice: the grouping is wrong, but the pickers still work.
   */
  adminGroupingUnavailable: boolean
  /** An EAGER notify write is in flight — disables the toggle alone. */
  isNotifySaving: boolean
  /** Any write in flight — disables the toggle AND both pickers. */
  isBusy: boolean
  onPrimaryChange: (ids: number[]) => void
  onAdditionalChange: (ids: number[]) => void
  onNotifyChange: (next: boolean) => void
  /**
   * Re-fire the recipients write after a failure. NOT the same as commit().
   *
   *   context: on the EAGER path commit() cannot retry — its recipients branch is
   *   gated on recipientsDirty, which is false whenever the picker matches the
   *   persisted set. Pick Sam (PUT fails), put Jane back (PUT fails): the
   *   selection now equals the baseline, so commit() issues nothing and resolves
   *   TRUE while the error banner stays up and Finalize stays blocked. This fires
   *   unconditionally instead — except with no baseline, where the picker is empty
   *   because hydration bailed, and re-firing would PUT that emptiness over the
   *   merchant's saved recipients.
   */
  retry: () => void

  // Host gating.
  isLoading: boolean
  /** merchant-users GET failed: no options to choose, so the host hard-errors. */
  isUsersError: boolean
  /**
   * The recipients cannot be saved right now — warning banner + Save disabled.
   * Covers a 403/404 AND any other settled failure that left no baseline.
   */
  contactsUnavailable: boolean
  /**
   * NO persisted baseline, so a recipient edit can neither be dirty-compared nor
   * PUT. The host passes this to the body to disable the pickers.
   *
   *   context: distinct from contactsUnavailable, which is an advisory that
   *   survives a retained-data refetch — there the pickers deliberately stay live
   *   for review. Here there is nothing to review: hydration bails on `!contacts`,
   *   so both pickers render EMPTY. Leaving them editable is what let a merchant
   *   pick someone, arm Save via the notify toggle, and be told "saved" over a
   *   recipient write that was skipped.
   */
  recipientsReadOnly: boolean

  // Banners.
  saveSuccess: boolean
  /** Recipients landed and only the notify config PUT failed. */
  isNotifyOnlySaveError: boolean
}

/**
 * The ONE escalation-contacts data layer: fetch, hydrate, dirty-track and
 * persist the recipient set plus the notify-location-managers flag.
 *
 *   context: three surfaces share it — the Settings tab and the Activation modal
 *   stage everything behind commit(), the wizard step writes per change and has
 *   no Save button (L13/L27). Both policies are opt-in options rather than a
 *   forked hook, because a second implementation of this is exactly the drift
 *   the shared-body work exists to remove.
 */
export function useEscalationContactsController(
  options: EscalationContactsControllerOptions
): EscalationContactsController {
  const { notifyMode, recipientMode = 'staged', saveKeys } = options
  const contactsSaveKey = saveKeys?.contacts
  const configSaveKey = saveKeys?.config

  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  // #3883's RBAC-aware ADMINS grouping, resolved once for all three surfaces.
  const {
    isAdmin: isUserAdmin,
    isLoading: adminContextLoading,
    isUnavailable: adminGroupingUnavailable,
  } = useAdminPredicate()

  const {
    data: usersData,
    isLoading: usersLoading,
    isError: isUsersError,
  } = merchantUsersApi.useGetMerchantUsersQuery({})
  const {
    data: contacts,
    isLoading: contactsLoading,
    error: contactsError,
  } = useGetEscalationContactsQuery()
  const { data: config, isLoading: isConfigLoading } =
    useGetRecoveryConfigQuery()

  const [
    setContacts,
    {
      isLoading: isSavingContacts,
      isError: isContactsSaveError,
      isSuccess: isContactsSaveSuccess,
      reset,
    },
  ] = useSetEscalationContactsMutation({ fixedCacheKey: contactsSaveKey })
  const [
    updateConfig,
    {
      isLoading: isSavingConfig,
      isError: isConfigSaveError,
      isSuccess: isConfigSaveSuccess,
      reset: resetConfig,
    },
  ] = useUpdateRecoveryConfigMutation({ fixedCacheKey: configSaveKey })

  // Both writes count as ONE operation (mirrors ActiveLocationsPane), so Save
  // and every dismiss path stay blocked across the whole sequence instead of
  // re-opening between the recipients PUT and the config PUT.
  const isSaving = isSavingContacts || isSavingConfig
  // The eager toggle write only. A staged host never sees this true: its config
  // PUT runs inside commit(), where isSaving already disables everything.
  const isNotifySaving = notifyMode === 'eager' && isSavingConfig
  // isNotifySaving is a strict subset of isSaving, so this IS isSaving. The two
  // stay separate members because the BODY needs the distinction — isNotifySaving
  // disables the toggle alone, isBusy disables the pickers too.
  const isBusy = isSaving

  // Memoized so the `?? []` fallback doesn't hand a fresh array identity to the
  // knownUserIds memo every render (RTK Query keeps usersData stable).
  const users = useMemo(() => usersData?.merchant_users ?? [], [usersData])
  // The ids still resolvable to a current merchant user. A recipient deleted
  // from the org drops out of both the staged payload and the dirty compare, so
  // a dead id is never re-PUT and never reads as an unsaved change.
  const knownUserIds = useMemo(
    () => new Set(users.map(user => user.id)),
    [users]
  )

  // Settled with no baseline. `contactsLoading` is RTK's first-load-only flag, so
  // a retained-data refetch does not trip this — which is what keeps the
  // "review what's listed" case editable while the empty case is not.
  const recipientsReadOnly = !contactsLoading && !contacts
  // A 403/404 is the DEMOTED advisory; any other settled failure that left no
  // baseline is equally unsaveable, so both disable Save and raise a notice. Kept
  // as one flag because every host gates Save on it identically.
  const contactsUnavailable =
    isContactsUnavailable(contactsError) || recipientsReadOnly

  const { primaryIds, additionalIds, setPrimaryIds, setAdditionalIds } =
    useHydratedContactSelection(contacts, isSaving)

  const serverNotifyManagers =
    config?.escalation_routing_notify_location_managers ?? false
  // One state variable serves both policies — a staged edit and an optimistic
  // mirror are the same shape with different lifetimes.
  const [notifyDraft, setNotifyDraft] = useState<boolean | null>(null)
  // Has the merchant edited the recipient selection at all? Only used to keep
  // commit() honest — see the guard there.
  const [recipientsTouched, setRecipientsTouched] = useState(false)
  const notifyLocationManagers = notifyDraft ?? serverNotifyManagers

  // EAGER ONLY. In staged mode this would silently discard a staged edit the
  // moment any background config refetch landed.
  //
  // The isConfigLoading guard is required, not decorative: serverNotifyManagers
  // reads `?? false` during load, so without it the effect fires on a LOADING
  // transition (undefined→false→true) rather than on a real server change.
  useEffect(() => {
    if (isConfigLoading) return
    if (notifyMode === 'eager') setNotifyDraft(null)
  }, [serverNotifyManagers, notifyMode, isConfigLoading])

  const reconcile = useCallback(
    (nextPrimary: number[], nextAdditional: number[]): EscalationContacts => ({
      primaryUserId: nextPrimary.find(id => knownUserIds.has(id)) ?? null,
      additionalUserIds: nextAdditional.filter(id => knownUserIds.has(id)),
    }),
    [knownUserIds]
  )

  const staged = useMemo(
    () => reconcile(primaryIds, additionalIds),
    [reconcile, primaryIds, additionalIds]
  )

  const recipientsDirty = useMemo(() => {
    // Fail CLOSED with no persisted baseline. A 500 on the contacts GET is not
    // 403/404, so `contactsUnavailable` is false and `contacts` is undefined —
    // without this, Save would PUT an empty set over the saved recipients.
    if (!contacts) return false
    const savedPrimary =
      contacts.primaryUserId !== null &&
      knownUserIds.has(contacts.primaryUserId)
        ? contacts.primaryUserId
        : null
    if (savedPrimary !== staged.primaryUserId) return true
    return !sameIdSet(
      contacts.additionalUserIds.filter(id => knownUserIds.has(id)),
      staged.additionalUserIds
    )
  }, [contacts, knownUserIds, staged])

  // The toggle participates in dirtiness ONLY when staged. In eager mode it is
  // already persisted, so folding it in would leave the surface permanently
  // dirty after one click. `null` = no staged flip pending.
  const stagedNotify =
    notifyMode === 'staged' &&
    notifyDraft !== null &&
    notifyDraft !== serverNotifyManagers
      ? notifyDraft
      : null

  const isDirty = recipientsDirty || stagedNotify !== null

  // Editing makes a prior save stale, so a green "Saved" banner never hangs over
  // an unsaved change. Skipped on the eager path: the write that follows
  // immediately moves the (possibly SHARED) mutation entry to pending anyway.
  //
  //   context: the condition must cover BOTH mutations' success, not just the
  //   contacts one. Under L13 a staged notify-only save fires the config PUT
  //   alone, so isContactsSaveSuccess is false while the banner is up, and a
  //   contacts-only check would leave it hanging over the next edit. The shipped
  //   pane could get away with two terms because its saveSuccess was
  //   contacts-only; folding the toggle into Save is what changed that.
  const clearStaleFeedback = useCallback(() => {
    if (
      isContactsSaveSuccess ||
      isConfigSaveSuccess ||
      isContactsSaveError ||
      isConfigSaveError
    ) {
      reset()
      resetConfig()
    }
  }, [
    isContactsSaveSuccess,
    isConfigSaveSuccess,
    isContactsSaveError,
    isConfigSaveError,
    reset,
    resetConfig,
  ])

  const persistRecipients = useCallback(
    (nextPrimary: number[], nextAdditional: number[]) => {
      setContacts(reconcile(nextPrimary, nextAdditional))
        .unwrap()
        // Failure does NOT revert the picker — the merchant's choice is kept and
        // surfaced through isSaveError, with Retry re-firing the write.
        .catch(() => {})
    },
    [setContacts, reconcile]
  )

  const onPrimaryChange = useCallback(
    (ids: number[]) => {
      setPrimaryIds(ids)
      setRecipientsTouched(true)
      // Build the replacement payload from the INCOMING ids, never by reading
      // state back after the setter (that value is a render behind).
      if (recipientMode === 'eager') {
        persistRecipients(ids, additionalIds)
        return
      }
      clearStaleFeedback()
    },
    [
      setPrimaryIds,
      recipientMode,
      persistRecipients,
      additionalIds,
      clearStaleFeedback,
    ]
  )

  const onAdditionalChange = useCallback(
    (ids: number[]) => {
      setAdditionalIds(ids)
      setRecipientsTouched(true)
      if (recipientMode === 'eager') {
        persistRecipients(primaryIds, ids)
        return
      }
      clearStaleFeedback()
    },
    [
      setAdditionalIds,
      recipientMode,
      persistRecipients,
      primaryIds,
      clearStaleFeedback,
    ]
  )

  const onNotifyChange = useCallback(
    (next: boolean) => {
      setNotifyDraft(next)
      if (notifyMode !== 'eager') {
        // Staging the toggle IS an edit under L13, so it invalidates a prior
        // save's banner exactly as a recipient change does. Without this the
        // surface shows "Saved" over a dirty form with Save re-enabled.
        clearStaleFeedback()
        return
      }
      updateConfig({ escalation_routing_notify_location_managers: next })
        .unwrap()
        // Unlike the pickers the toggle DOES revert: it is a two-state control
        // whose rendered value would otherwise lie about what is persisted.
        .catch(() => setNotifyDraft(null))
    },
    [notifyMode, updateConfig, clearStaleFeedback]
  )

  const commit = useCallback(async (): Promise<boolean> => {
    // Refuse rather than silently skip. recipientsDirty fails CLOSED with no
    // baseline, but stagedNotify does not and isDirty is the OR of the two — so a
    // merchant whose contacts GET failed could pick a recipient, arm Save with the
    // notify toggle, and get "Escalation contacts saved" over a recipients write
    // that never fired. Returning false is what stops the host announcing a
    // success for a write it deliberately dropped.
    if (recipientsTouched && !contacts) return false
    try {
      // Primary intent first, sequential and never Promise.all: a failed notify
      // PUT must leave the already-saved recipients intact, and the retry then
      // runs config-only because recipientsDirty is false by then.
      if (recipientsDirty) await setContacts(staged).unwrap()
      if (stagedNotify !== null) {
        await updateConfig({
          escalation_routing_notify_location_managers: stagedNotify,
        }).unwrap()
      }
      return true
    } catch {
      // Staged edits are RETAINED so the merchant can retry or Cancel; the
      // failure surfaces through isSaveError / isNotifyOnlySaveError.
      return false
    }
  }, [
    recipientsTouched,
    contacts,
    recipientsDirty,
    staged,
    setContacts,
    stagedNotify,
    updateConfig,
  ])

  const retry = useCallback(() => {
    // Nothing to re-fire over: the pickers are read-only in this state, so an
    // empty selection is hydration bailing, not the merchant clearing it, and a
    // REPLACE PUT would wipe the saved set from a button labelled "Try again".
    if (!contacts) return
    if (recipientMode === 'eager') {
      // Unconditional by design — see the `retry` doc on the controller type.
      persistRecipients(primaryIds, additionalIds)
      return
    }
    // A staged host retries through its own Save, where recipientsDirty is still
    // true after a failure, so commit() is already the right call.
    void commit()
  }, [
    contacts,
    recipientMode,
    persistRecipients,
    primaryIds,
    additionalIds,
    commit,
  ])

  const rollback = useCallback(() => {
    setNotifyDraft(null)
    setPrimaryIds(
      contacts?.primaryUserId != null ? [contacts.primaryUserId] : []
    )
    setAdditionalIds(contacts?.additionalUserIds ?? [])
    reset()
    resetConfig()
  }, [contacts, setPrimaryIds, setAdditionalIds, reset, resetConfig])

  // NO unmount reset here, deliberately. RTK already clears a mutation entry on
  // unmount unless a fixedCacheKey is set, so a hand-rolled
  // `useEffect(() => () => reset(), [reset])` is not merely redundant: `reset`'s
  // identity churns on every trigger, so React runs the PREVIOUS cleanup and
  // blanks isSaving/isError/isSuccess for the whole of a RETRY.
  //
  //   context: that presents as a silent save — no spinner, no banner either way
  //   — and leaves ConfigModalHost's in-flight dismiss block inert, so Escape
  //   tears the modal down mid-PUT and rolls back. Under a shared fixedCacheKey
  //   the entry outlives this hook ON PURPOSE: the observer watching it owns the
  //   reset. Pinned by useEscalationContactsController.mutationLifecycle.test.ts,
  //   which needs a real store — a mocked `reset` cannot see any of this.

  const isSaveError = isContactsSaveError || isConfigSaveError
  // Recipients landed and only the secondary flag failed — collapsing this into
  // the generic error would falsely imply the selection was lost.
  const isNotifyOnlySaveError = isConfigSaveError && !isContactsSaveError
  // A notify-only staged save fires no contacts PUT, so the config mutation's
  // success has to count too — but only where that write IS part of commit();
  // in eager mode it fires on every click and would flash a spurious banner.
  const saveSuccess =
    isContactsSaveSuccess || (notifyMode === 'staged' && isConfigSaveSuccess)

  return useMemo(
    () => ({
      users,
      primaryIds,
      additionalIds,
      notifyLocationManagers,
      canManage,
      isUserAdmin,
      adminGroupingUnavailable,
      isNotifySaving,
      isBusy,
      onPrimaryChange,
      onAdditionalChange,
      onNotifyChange,
      retry,
      // adminContextLoading is folded in (#3883's pane did the same): the ADMINS
      // grouping is the only thing separating the two picker groups, so rendering
      // before it resolves shows every admin in ALL OTHER USERS and then reshuffles.
      isLoading:
        usersLoading ||
        contactsLoading ||
        isConfigLoading ||
        adminContextLoading,
      isUsersError,
      contactsUnavailable,
      recipientsReadOnly,
      saveSuccess,
      isNotifyOnlySaveError,
      isDirty,
      isSaving,
      isSaveError,
      commit,
      rollback,
    }),
    [
      users,
      primaryIds,
      additionalIds,
      notifyLocationManagers,
      canManage,
      isUserAdmin,
      adminGroupingUnavailable,
      isNotifySaving,
      isBusy,
      onPrimaryChange,
      onAdditionalChange,
      onNotifyChange,
      retry,
      usersLoading,
      contactsLoading,
      isConfigLoading,
      adminContextLoading,
      isUsersError,
      contactsUnavailable,
      recipientsReadOnly,
      saveSuccess,
      isNotifyOnlySaveError,
      isDirty,
      isSaving,
      isSaveError,
      commit,
      rollback,
    ]
  )
}
